import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import {openSocialAccountConnectionModal, PANEL_ID} from '@Modals/social-accounts-connection-modal/social-accounts-connection-modal-library';
import {AddAccount} from '@Components/social-media/components/add-account';
import {InputField} from '@Components/input-field-v2';
import {InputControlItemType, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {
  useAccountIdsToPublishTo,
  useDoesWizardPostHaveSavedAccounts,
  useEmphasizedPlatform,
  useLoadSocialAccountsForWizard,
  useSocialMediaWizardAccounts,
} from '@Components/social-media/social-media.hooks';
import {SocialAccountListItem} from '@Components/social-media-wizard/components/social-account-list-item';
import {Text, TextSize} from '@Components/text';
import {SelectableOptionRow} from '@Components/social-media-wizard/components/selectable-option-row/selectable-option-row';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  isAccountInSearch,
  isEligibleForSocialAccountsUpsellDialog,
  openSocialAccountsUpsellDialog,
  removeConnectAccountBroadcastChannel,
  sortPlanLockedAccountsLast,
} from '@Libraries/social-media-account.library';
import {
  addAccountIdForPublishing,
  disableNextButton,
  enableNextButton,
  removeAccountIdFromPublishing,
  updateAccountIdsToPublishTo,
  updateEmphasizedPlatform,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getAccountForId, getAccountPublishabilityInfo, getAccountsAvailableForWizard, toSortedUniqueAccountIds} from '@Libraries/social-media-wizard-accounts-library';
import type {AccountPublishabilityInfo} from '@Libraries/social-media-wizard-accounts-library';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {
  useAccountLevelErrorMessage,
  useAreAccountsLoading,
  useDesignsToPublish,
  useIsGoogleBusinessProfileEnabled,
  useNeedsResizeMap,
  useNumParentAccountsConnected,
  useWizardDesignsGraphicsMetaMap,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {compareByReconnectFirst} from '@Libraries/social-media-error.library';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {MoreAccountsUpsell} from '@Components/more-accounts-upsell';
import {ErrorBox} from '@Components/error-box';
import {SocialPlatformConnectGrid} from '@Components/social-media/components/social-platform-connect-grid/social-platform-connect-grid';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import styles from './social-accounts-list.module.scss';
import {useAppDispatch} from '@/hooks';
import {useAccountPreselection, useReloadAccountsAndOfferSelection} from './social-accounts-list.hooks';
import type {SocialAccountsListProps} from './social-accounts-list.types';
import {NUM_LOADING_ACCOUNT_ROWS} from './social-accounts-list.types';

export function SocialAccountsList({controlledSelection, onAddAccountClick}: SocialAccountsListProps): ReactElement {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const selectedAccountIds = controlledSelection?.selectedAccountIds ?? accountIdsToPublishTo;
  const isSelectionControlled = controlledSelection !== undefined;
  const hasSavedAccountSelection = useDoesWizardPostHaveSavedAccounts();
  const accountLevelErrorMessage = useAccountLevelErrorMessage();
  const emphasizedPlatform = useEmphasizedPlatform();
  const [isListLoaded, setIsListLoaded] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [activePlatformToConnect, setActivePlatformToConnect] = useState<SocialPlatform | null>(null);
  const {isLargeScreen, isExtraLargeScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const dispatch = useAppDispatch();
  const numSelectedAccounts = selectedAccountIds.length;
  const areAccountsLoading = useAreAccountsLoading();
  const numParentAccountsConnected = useNumParentAccountsConnected();
  const posters = useDesignsToPublish();
  const designsGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const accountNeedsResizeMapForDesigns = useNeedsResizeMap();
  const isGoogleBusinessProfileEnabled = useIsGoogleBusinessProfileEnabled();
  const reloadAccountsAndOfferSelection = useReloadAccountsAndOfferSelection();

  const getPublishabilityForAccount = (account: SocialAccount): AccountPublishabilityInfo => {
    return getAccountPublishabilityInfo({
      account,
      posters,
      accountNeedsResizeMap: accountNeedsResizeMapForDesigns,
      designsGraphicsMetaMap,
      isGraphicsMetaLoading: isGraphicsMetaLoadingState(),
    });
  };

  const isGraphicsMetaLoadingState = (): boolean => {
    if (Object.keys(designsGraphicsMetaMap).length === 0) {
      return true;
    }
    return Object.values(designsGraphicsMetaMap)
      .flatMap((pageMeta) => Object.values(pageMeta))
      .some((graphicMeta) => graphicMeta.graphicURL === '');
  };

  const getAvailableSocialAccounts = (): SocialAccount[] => {
    return sortPlanLockedAccountsLast(getAccountsAvailableForWizard(accounts, isGoogleBusinessProfileEnabled));
  };

  const availableAccounts = getAvailableSocialAccounts();

  const toggleButtonByAccounts = (): void => {
    if (isSelectionControlled) {
      return;
    }

    if (numSelectedAccounts > 0) {
      dispatch(enableNextButton());
    } else {
      dispatch(disableNextButton());
    }
  };

  useEffect(() => {
    toggleButtonByAccounts();
  }, []);

  useEffect(() => {
    toggleButtonByAccounts();

    return (): void => {
      if (isSelectionControlled) {
        return;
      }

      dispatch(enableNextButton());
    };
  }, [numSelectedAccounts]);

  useDidUpdateEffect(() => {
    if (isSelectionControlled) {
      return;
    }

    if (accounts.length === 0) {
      dispatch(disableNextButton());
    }
  }, [accounts, accountSearchTerm]);

  useLoadSocialAccountsForWizard();

  const doReloadAccounts = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);

    handleAccountConnectionResponse(connectionResponse, () => {
      void reloadAccountsAndOfferSelection(connectionResponse?.socialMediaPlatform, {advanceToReviewOnFirstConnection: true});
    });
  };

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(doReloadAccounts);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, []);

  const isAccountSelected = (account: SocialAccount): boolean => {
    return selectedAccountIds.includes(account.id) && isAccountPublishable(account.id);
  };

  const isAccountPublishable = (accountId: number): boolean => {
    const currentAccount = getAccountForId(accounts, accountId);

    if (!currentAccount) {
      return false;
    }

    return getPublishabilityForAccount(currentAccount).isPublishable;
  };

  const getAccountsOrderedForDisplay = (): SocialAccount[] => {
    return [...availableAccounts].sort(compareByReconnectFirst);
  };

  const getAccountListBody = (): ReactElement[] => {
    if (areAccountsLoading) {
      return getLoadingAccounts();
    }

    return getListItems();
  };

  const getListItems = (): ReactElement[] => {
    const accountCheckItems: ReactElement[] = [];

    getAccountsOrderedForDisplay().forEach((account) => {
      const {isPublishable, tooltip} = getPublishabilityForAccount(account);

      accountCheckItems.push(
        <li key={`list-${account.id}`} className={`spacing-m-b-3 _unpadded ${!isAccountInSearch(accountSearchTerm, account.name) ? '_hidden' : ''}`}>
          <SocialAccountListItem
            key={`social-account-item-${account.id}`}
            account={account}
            tooltip={tooltip}
            isDisabled={!isPublishable}
            isSelected={isAccountSelected(account)}
            onCheck={onAccountChecked}
            onUncheck={onAccountUnchecked}
          />
        </li>
      );
    });

    return accountCheckItems;
  };

  const onAddAccountRequested = (): void => {
    if (onAddAccountClick) {
      onAddAccountClick();
      return;
    }

    openSocialAccountConnectionModal({panelId: PANEL_ID, title: window.i18next.t('pmwjs_connect_your_accounts')});
  };

  const onAccountChecked = (idAccount: number): void => {
    selectAccount(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountUnchecked = (idAccount: number): void => {
    unselectAccount(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountSelectionUpdated = (): void => {
    if (isSelectionControlled) {
      return;
    }

    dispatch(updateEmphasizedPlatform(undefined));
  };

  const selectAccount = (idAccount: number): void => {
    if (!controlledSelection) {
      dispatch(addAccountIdForPublishing(idAccount));
      return;
    }

    controlledSelection.onSelectedAccountIdsChange(toSortedUniqueAccountIds([...controlledSelection.selectedAccountIds, idAccount]));
  };

  const unselectAccount = (idAccount: number): void => {
    if (!controlledSelection) {
      dispatch(removeAccountIdFromPublishing(idAccount));
      return;
    }

    controlledSelection.onSelectedAccountIdsChange(
      controlledSelection.selectedAccountIds.filter((accountId) => {
        return accountId !== idAccount;
      })
    );
  };

  const replaceSelection = (accountIds: number[]): void => {
    if (!controlledSelection) {
      dispatch(updateAccountIdsToPublishTo(accountIds));
      return;
    }

    controlledSelection.onSelectedAccountIdsChange(toSortedUniqueAccountIds(accountIds));
  };

  useAccountPreselection({
    accounts,
    availableAccounts,
    accountIdsToPublishTo: selectedAccountIds,
    accountNeedsResizeMapForDesigns,
    emphasizedPlatform,
    isListLoaded,
    areAccountsLoading,
    setIsListLoaded,
    isSelectionControlled,
    hasSavedAccountSelection,
    isAccountPublishable,
    onAccountSelected: selectAccount,
    replaceSelection,
  });

  const onSelectAll = (): void => {
    if (areAccountsLoading) {
      return;
    }

    replaceSelection(areAllSelectableAccountsSelected() ? [] : getAllSelectableAccountIds());
    onAccountSelectionUpdated();
  };

  const getAllSelectableAccountIds = (): number[] => {
    const accountIds: number[] = [];

    availableAccounts.forEach((account) => {
      if (isAccountPublishable(account.id)) {
        accountIds.push(account.id);
      }
    });

    return accountIds;
  };

  const areAllSelectableAccountsSelected = (): boolean => {
    const selectableAccountIds = getAllSelectableAccountIds();

    return (
      selectableAccountIds.length > 0 &&
      selectableAccountIds.every((accountId) => {
        return selectedAccountIds.includes(accountId);
      })
    );
  };

  const getSelectAllControl = (): ReactElement => {
    return (
      <SelectableOptionRow
        className={`_cursor-pointer flex-h-row align-items-center ${styles.selectAllControl}`}
        checkboxClasses={`spacing-m-r-2 ${styles.selectAllCheckbox}`}
        isChecked={areAllSelectableAccountsSelected()}
        onToggle={onSelectAll}
        checkboxAriaLabel={window.i18next.t('pmwjs_select_all')}
      >
        <Text val={window.i18next.t('pmwjs_select_all')} size={TextSize.MD} className="ct-neutral-primary" />
        <Text val={`(${numSelectedAccounts})`} size={TextSize.MD} className="spacing-m-l-1 ct-neutral-primary" bold />
      </SelectableOptionRow>
    );
  };

  const onSearchInputChange = (searchTerm: string): void => {
    setAccountSearchTerm(searchTerm);
  };

  const getLoadingAccounts = (): ReactElement[] => {
    const loadingAccounts = [];

    for (let i = 0; i < NUM_LOADING_ACCOUNT_ROWS; i++) {
      loadingAccounts.push(
        <li className="spacing-m-b-3 _unpadded" key={`skeleton-loader-${i}`}>
          <div className={`flex-row-align-center ${styles.loadingAccountContainer}`}>
            <SkeletonBrick height={20} width={20} className={`spacing-m-r-2 ${styles.loadingAccountIndex}`} />
            <SkeletonBrick height={32} width={32} className={`spacing-m-r-1 ${styles.loadingAccountPicture}`} />
            <SkeletonBrick height={16} className={styles.loadingAccountDetails} />
          </div>
        </li>
      );
    }

    return loadingAccounts;
  };

  const getAddAccountButton = (): ReactElement => {
    if (isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      return (
        <div className={`${styles.addAccountButtonSlot} spacing-p-t-2 spacing-p-b-2`}>
          <MoreAccountsUpsell />
        </div>
      );
    }

    return (
      <div className={`${styles.addAccountButtonSlot} spacing-p-t-2 spacing-p-b-2`}>
        <AddAccount onCustomAdd={onAddAccountRequested} iconClasses={`${styles.addAccountTile} _unmargin-left`} className={styles.addAccountRow} />
      </div>
    );
  };

  const onSearchClear = (): void => {
    setAccountSearchTerm('');
  };

  const getSearchBarElement = (): ReactElement => {
    const hasQuery = accountSearchTerm !== '';

    return (
      <div className={styles.searchFieldSlot}>
        <InputField
          input={accountSearchTerm}
          onInputChange={(value): void => {
            onSearchInputChange(String(value));
          }}
          placeholder={window.i18next.t('pmwjs_search_accounts')}
          inputType={InputFieldType.SEARCH}
          className="spacing-m-b-3"
          inputClassName={`corner-radius-8 ${styles.searchInput}`}
          controls={{
            showDefaultControls: false,
            customControls: [
              {
                type: InputControlItemType.ICONS_ONLY,
                id: 'account-search-icon',
                icon: hasQuery ? 'icon-close' : 'icon-search',
                containerClassName: styles.searchIcon,
              },
            ],
            onClick: onSearchClear,
          }}
        />
      </div>
    );
  };

  const getErrorMessageBox = (): ReactElement | null => {
    if (accountLevelErrorMessage.title === '' && accountLevelErrorMessage.description === '') {
      return null;
    }

    return (
      <div className="spacing-m-b-4">
        <ErrorBox title={accountLevelErrorMessage.title} description={accountLevelErrorMessage.description} />
      </div>
    );
  };

  const onEmptyStatePlatformClick = (platform: SocialPlatform): void => {
    if (isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      openSocialAccountsUpsellDialog(UpsellDialogEntryPoint.SOCIAL_WIZARD_ACCOUNTS_LIST);
      return;
    }

    setActivePlatformToConnect(platform);
  };

  const clearActivePlatformToConnect = (): void => {
    setActivePlatformToConnect(null);
  };

  const hasNoAvailableAccounts = (): boolean => {
    return !areAccountsLoading && availableAccounts.length === 0;
  };

  const getEmptyStateConnectGrid = (): ReactElement => {
    return (
      <div className={styles.emptyStateConnectGrid}>
        <SocialPlatformConnectGrid
          activePlatform={activePlatformToConnect}
          isMobileLayout={!isLargeScreen && !isExtraLargeScreen}
          onPlatformCardClick={onEmptyStatePlatformClick}
          onCancelConnection={clearActivePlatformToConnect}
          onAccountConnected={clearActivePlatformToConnect}
        />
      </div>
    );
  };

  const getPopulatedAccountsBody = (): ReactElement => {
    return (
      <>
        {getSearchBarElement()}
        <div className={`spacing-p-b-3 ${styles.controlsRow}`}>
          {getAddAccountButton()}
          {getSelectAllControl()}
        </div>
        <ul id="select-accounts-onboarding-container" className="spacing-m-t-1">
          {getAccountListBody()}
        </ul>
      </>
    );
  };

  const getListBody = (): ReactElement => {
    if (hasNoAvailableAccounts()) {
      return getEmptyStateConnectGrid();
    }

    return getPopulatedAccountsBody();
  };

  return (
    <div>
      {getErrorMessageBox()}
      {getListBody()}
    </div>
  );
}
